import React, { useReducer } from 'react';

import _ from 'lodash';
import { Dialog, DialogContent, Grid } from '@material-ui/core';
import { MenuItem, TextField, useTheme } from '@material-ui/core';
import { BsPatchCheckFill, BsExclamationCircleFill } from 'react-icons/bs';

import API from 'utils/api';
import { useDialog, useSources } from 'hooks';
import { DialogActions, DialogTitle, Progress, Select } from 'components';
import { call } from 'utils';

import styles from './CreateTagDialog.module.scss';

const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT': {
      const categories = Array.isArray(action.payload) ? action.payload : [];

      return {
        categories,
        selected: categories.find(it => it.tagCategory === 'no category'),
        status: 'active',
        value: '',
      };
    }

    case 'SET_STATUS':
      return { ...state, status: action.payload };

    case 'SET_CATEGORY':
      return { ...state, selected: action.payload };

    case 'SET_VALUE':
      return { ...state, value: action.payload };

    case 'SET_RESPONSE': {
      const tag = action.payload;

      return {
        ...state,
        tag: tag
          ? {
              category: { id: tag.tagCategory, text: state.selected.tagCategory },
              operator: 'is',
              value: { id: tag._id, text: tag.tagName },
            }
          : null,
        status: tag ? 'done' : 'error',
      };
    }

    default:
      return state;
  }
};

const CreateTagDialog = ({ open, onClose, onCreate, labels }) => {
  const [state, dispatch] = useReducer(reducer, {});
  const { status, selected, categories, value } = state;

  const sources = useSources();
  const theme = useTheme();

  const { isOpening } = useDialog(open, {
    onOpen: () => {
      dispatch({ type: 'SET_STATUS', payload: 'loading' });

      API.getTagCategories(sources).then(({ data, canceled }) => {
        if (!canceled) {
          dispatch(
            data
              ? { type: 'INIT', payload: data }
              : { type: 'SET_STATUS', payload: 'error' }
          );
        }
      });
    },
    onClose: () => sources.cancelAll(),
  });

  const createTag = () => {
    dispatch({ type: 'SET_STATUS', payload: 'submitting' });

    API.createTag(
      {
        tagCategory: selected.tagCategory,
        tag: _.trim(value),
        deleteRestrict: false,
      },
      sources
    ).then(({ data }) => dispatch({ type: 'SET_RESPONSE', payload: data }));
  };

  const renderFields = () => (
    <Grid item xs={12}>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Select
            label="Tag category"
            value={selected}
            onChange={e => dispatch({ type: 'SET_CATEGORY', payload: e.target.value })}
            fullWidth
          >
            {categories.map(it => (
              <MenuItem key={it._id} value={it}>
                {it.tagCategory}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Tag value"
            value={value}
            onChange={e => dispatch({ type: 'SET_VALUE', payload: e.target.value })}
            fullWidth
          />
        </Grid>
      </Grid>
    </Grid>
  );

  const renderResult = () => {
    let Icon, message, color;

    switch (status) {
      case 'done':
        Icon = BsPatchCheckFill;
        message = 'Success';
        color = theme.palette.success.main;
        break;

      default:
        Icon = BsExclamationCircleFill;
        message = 'Something went wrong!';
        color = theme.palette.error.main;
        break;
    }

    return (
      <div className={styles.message}>
        <Icon style={{ color }} />
        <span>{message}</span>
      </div>
    );
  };

  const getActions = () => {
    switch (status) {
      case 'active':
        return [
          { label: 'cancel', onClick: onClose },
          {
            label: 'create',
            onClick: createTag,
            disabled: !selected || !_.trim(value),
            type: 'submit',
          },
        ];

      case 'done':
        return [
          {
            label: _.get(labels, 'done', 'add'),
            type: 'submit',
            onClick: () => call(onCreate, state.tag),
          },
        ];

      case 'error':
        return [
          { label: _.get(labels, 'error', 'close'), type: 'submit', onClick: onClose },
        ];

      default:
        return [];
    }
  };

  const onCloseDialog = status !== 'submitting' && status !== 'done' ? onClose : null;

  return (
    <Dialog open={open} onClose={onCloseDialog} classes={{ paper: styles.dialog }}>
      <DialogTitle onClose={onCloseDialog}>Create tag</DialogTitle>

      <DialogContent className={styles.content}>
        {isOpening || status === 'loading' || status === 'submitting' ? (
          <Progress />
        ) : status === 'active' ? (
          renderFields()
        ) : (
          renderResult()
        )}
      </DialogContent>

      <DialogActions actions={getActions()} />
    </Dialog>
  );
};

export default CreateTagDialog;
